import React, { useEffect, useState } from 'react';

import { Divider } from '@chakra-ui/react';
import {
  Card,
  CardContent,
  CardHeader,
  Goal,
  MyOneOnOne,
  Reviews,
  Engagement,
  Feedback,
  Product,
  Develop,
} from '@mesh.ai/rabbit';
import { useGetFeatureManagementData } from 'Deer/hooks/dataHooks/featureManagement/useGetFeatureManagementData';
import { capitalize } from 'lodash';
import { useSelector } from 'react-redux';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { sendPageViewEvent } from 'constants/mixpanelHelper';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextHeader2 } from 'uiToolkitV1/Typography';

import { ModuleItems } from './components/ModuleItems';

interface Module {
  key: string;
  heading: string;
  description: string;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  moduleState?: 'DISABLED' | 'ENABLED' | 'NOT_SUPPORTED';
  isToggleAble?: boolean;
  tooltipMessage?: string;
  moduleKey?: string;
}

interface ModulesState {
  [key: string]: string;
}

const MODULE_DATA: Module[] = [
  {
    key: 'GOAL',
    heading: 'Goals & OKRs',
    description: 'Driven by visible progress and personalized nudges, not process compliance',
    icon: Goal,
  },
  {
    key: 'FEEDBACK',
    heading: 'Praise & Advice',
    description: 'Move away from periodic feedback to realtime social appreciation',
    icon: Feedback,
  },
  {
    key: 'CHECK_IN',
    heading: '1:1 Conversations',
    description: 'Manage 1:1s that drive coaching and development',
    icon: MyOneOnOne,
  },
  {
    key: 'REVIEW',
    heading: 'Performance Reviews & 360',
    description: 'Transparent performance reviews without endless form filling',
    icon: Reviews,
  },
  {
    key: 'COMPETENCY_IDP',
    heading: 'Competencies & IDP (Develop)',
    description: 'Empower employees with clarity and means and to meet their career growth goals',
    icon: Develop,
  },
  {
    key: 'ENGAGEMENT',
    heading: 'Engagement & Pulse',
    description: 'Deeper understanding of your people to make your company stronger',
    icon: Engagement,
  },
];

export const ModuleSettingsV1 = () => {
  const { planType } = useSelector(selectLoggedInEmployeeData) as { planType: string };
  const { data: moduleData, isLoading, isError } = useGetFeatureManagementData();

  const [modulesState, setModulesState] = useState<ModulesState>({});

  useEffect(() => {
    if (moduleData) {
      setModulesState(moduleData);
    }
  }, [moduleData]);

  const updatedModuleData = MODULE_DATA.filter((module) => module.key in modulesState).map((module) => {
    const moduleState = modulesState[module.key] || 'DISABLED';
    let isToggleAble = true;
    let tooltipMessage = '';

    if (module.key === 'COMPETENCY_IDP') {
      if (modulesState.FEEDBACK === 'DISABLED') {
        isToggleAble = moduleState === 'ENABLED';
        if (moduleState !== 'ENABLED') {
          tooltipMessage = "To activate the 'Competencies & IDP', please first activate 'Praises & Advice'.";
        }
      }
    } else if (module.key === 'FEEDBACK') {
      if (modulesState.COMPETENCY_IDP === 'ENABLED') {
        isToggleAble = false;
        tooltipMessage = "To deactivate 'Praises & Advice', you must first deactivate the 'Competencies & IDP'.";
      }
    }

    return {
      ...module,
      moduleState,
      isToggleAble,
      tooltipMessage,
    };
  });

  const handleToggle = (key: string, newState: boolean) => {
    setModulesState((prevState) => {
      const newModulesState = { ...prevState, [key]: newState ? 'ENABLED' : 'DISABLED' };

      if (key === 'COMPETENCY_IDP' && newState) {
        newModulesState.FEEDBACK = 'ENABLED';
      } else if (key === 'FEEDBACK' && !newState) {
        newModulesState.COMPENTENCY_IDP = 'DISABLED';
      }

      return newModulesState;
    });
  };

  useEffect(() => {
    sendPageViewEvent({
      eventProperties: {
        source: 'Company-settings-module preferences',
      },
    });
  }, []);

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (isError) {
    return <EmptyScreen heading="No data to show." description="Nothing to see here yet." illustrationSrc="" />;
  }

  return (
    <>
      <TextHeader2 bold mr="8px" mb="8px">
        Subscription Details: {`${capitalize(planType)} Plan`}
      </TextHeader2>

      <Card>
        <CardHeader>
          <ListItemComp
            style={{
              cursor: 'default',
            }}
            withoutBackground
            title="Products"
            subTitle="Customize how competencies are assessed to fit your organization's unique structure and needs for effective performance evaluations"
            // eslint-disable-next-line react/no-unstable-nested-components
            LeftComponent={() => <Product color="#666666" />}
          />
        </CardHeader>
        <Divider color="neutral.F2F2F2" margin="10px 4px" />
        <CardContent>
          {updatedModuleData.map(
            ({ key, heading, description, moduleState, icon, isToggleAble, tooltipMessage }, idx, arr) => (
              <React.Fragment key={key}>
                <ModuleItems
                  heading={heading}
                  description={description}
                  icon={icon}
                  moduleState={moduleState}
                  isToggleAble={isToggleAble}
                  tooltipMessage={tooltipMessage}
                  moduleKey={key}
                  onToggle={(newState) => handleToggle(key, newState)}
                />
                {idx !== arr.length - 1 && <Divider color="neutral.F2F2F2" margin="10px 4px" />}
              </React.Fragment>
            )
          )}
        </CardContent>
      </Card>
    </>
  );
};
